import { confirmUnsavedChangesOnLeave } from '../../shared/components/unsaved-changes-dialog/unsaved-changes.registry';
import { NgModule } from '@angular/core';
import { provideRouter } from '@angular/router';
import { UserAuthGuard } from '../user/guards/user-auth-guard';
import { PermissionGuard } from '../../core/guards/permission.guard';
import {
  LayoutTwoColumnLeftComponent
} from '../common/components/layout-two-column-left/layout-two-column-left.component';


@NgModule({
  providers: [
    provideRouter([
      {
        path: 'administration',
        component: LayoutTwoColumnLeftComponent,
        canActivate: [ UserAuthGuard, PermissionGuard ],
        data: { permissions: ['ADMIN_GET_PROFILE', 'ADMIN_GET_USER', 'CHANGE_PASSWORD'] },
        children: [
          // redirect to company-profile by default
          {
            path: '',
            redirectTo: 'company-profile',
            pathMatch: 'full',
          },
          // side navigation
          {
            path: '',
            loadComponent: () => import('./components/navigation/navigation.component'),
            outlet: 'sidebar'
          },
          {
            path: 'company-profile',
            canActivate: [PermissionGuard],
            data: { permissions: 'ADMIN_GET_PROFILE' },
            loadComponent: () => import('./pages/company-profile/company-profile.component'),
            // Unsaved AutoPay settings / term rules must be saved or discarded before leaving.
            canDeactivate: [confirmUnsavedChangesOnLeave],
          },
          {
            path: 'user-access',
            canActivate: [PermissionGuard],
            data: { permissions: 'ADMIN_GET_USER' },
            loadComponent: () => import('./pages/user-access/user-access.component'),
          },
          {
            path: 'account-management',
            loadComponent: () => import('./pages/account-management/account-management.component'),
          },
          {
            path: 'settings',
            canActivate: [PermissionGuard],
            data: { permissions: 'CHANGE_PASSWORD' },
            loadComponent: () => import('./pages/settings/settings.component'),
          },
          {
            // ERP admins manage import/export definitions and SFTP connections here — the role
            // exists for exactly this. PermissionGuard is OR-by-default and PermissionService
            // short-circuits SYSTEM_ADMIN to true, so system admins keep their access.
            path: 'integrations',
            canActivate: [PermissionGuard],
            data: { permissions: ['SFTP_ERP_MANAGE', 'ERP_IMPORT_LIST', 'ERP_EXPORT_LIST'] },
            loadComponent: () => import('./pages/integrations/integrations.component'),
          },
          {
            // Channels, distribution lists and routing rules.
            //
            // Both permissions are currently in NO role group, so in practice
            // this resolves to SYSTEM_ADMIN only — PermissionService short-
            // circuits on a SYSTEM_ADMIN role, and nothing else can satisfy
            // either name. The array is kept as an OR (rather than replaced with
            // a literal 'SYSTEM_ADMIN') because it states the permission the page
            // actually needs: when the subsystem opens up, NOTIFICATION_MANAGE
            // returns to the admin bundles and this route starts working for
            // admins again with no change here.
            path: 'notifications',
            canActivate: [PermissionGuard],
            data: { permissions: ['NOTIFICATION_MANAGE', 'NOTIFICATION_VIEW'] },
            loadComponent: () => import('./pages/notifications/notifications.component'),
          },
          {
            // Captured (test-mode) emails for this profile. TEST_GET_EMAILS is a
            // specialised role in no group, granted per user — the view renders
            // message bodies, which for a profile's own customers means receipts,
            // password resets and FlashPay links. The endpoint additionally 404s
            // when test mode is off, so the page is honest even if someone reaches
            // it directly in an environment that captures nothing.
            path: 'test-emails',
            canActivate: [PermissionGuard],
            data: { permissions: 'TEST_GET_EMAILS' },
            loadComponent: () => import('./pages/test-emails/test-emails.component'),
          },
          {
            path: 'system-settings',
            canActivate: [PermissionGuard],
            data: { permissions: 'SYSTEM_ADMIN' },
            loadComponent: () => import('./pages/system-settings/system-settings.component'),
          },
          {
            path: 'scheduler',
            canActivate: [PermissionGuard],
            data: { permissions: 'SYSTEM_ADMIN' },
            loadComponent: () => import('./pages/scheduler/scheduler.component'),
          }
        ]
      }
    ])
  ]
})
export class AdminRoutingModule {
}
